import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Bug,
  Clock,
  CheckCircle,
  AlertTriangle,
  Eye,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Monitor,
  Smartphone,
  Chrome,
  Globe,
  Loader2,
  Languages,
} from 'lucide-react';
import { api } from '../../services/api';
import type { BugReport } from '../../services/api';

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  investigating: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700',
};

const CATEGORY_LABELS: Record<string, string> = {
  display_issue: 'Display Issue',
  crash_freeze: 'Crash / Freeze',
  data_content_error: 'Data Error',
  performance_problem: 'Performance',
  feature_not_working: 'Feature Broken',
  login_account_issue: 'Account Issue',
  label_scan_issue: 'Label Scan',
  other: 'Other',
};

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  web: <Globe className="w-4 h-4" />,
  mobile: <Smartphone className="w-4 h-4" />,
  extension: <Chrome className="w-4 h-4" />,
  admin: <Monitor className="w-4 h-4" />,
};

const STATUS_OPTIONS = ['new', 'investigating', 'resolved', 'closed'];
const CATEGORY_OPTIONS = Object.keys(CATEGORY_LABELS);
const PLATFORM_OPTIONS = ['web', 'mobile', 'extension', 'admin'];

export default function BugReports() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['admin-bug-report-stats'],
    queryFn: () => api.getAdminBugReportStats(),
  });

  // Fetch reports
  const {
    data: reportsData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['admin-bug-reports', page, statusFilter, categoryFilter, platformFilter, search],
    queryFn: () =>
      api.getAdminBugReports({
        page,
        limit: 20,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        platform: platformFilter || undefined,
        search: search || undefined,
      }),
  });

  // Fetch selected report
  const { data: reportDetails, isLoading: loadingDetails } = useQuery({
    queryKey: ['admin-bug-report', selectedReport],
    queryFn: () => (selectedReport ? api.getAdminBugReport(selectedReport) : null),
    enabled: !!selectedReport,
  });

  // Update report mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status?: string; admin_notes?: string } }) =>
      api.updateAdminBugReport(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-bug-report', selectedReport] });
      queryClient.invalidateQueries({ queryKey: ['admin-bug-reports'] });
      queryClient.invalidateQueries({ queryKey: ['admin-bug-report-stats'] });
    },
  });

  // AI Analysis mutation
  const analyzeMutation = useMutation({
    mutationFn: () =>
      api.analyzeAdminBugReports({
        filters: {
          status: statusFilter ? [statusFilter] : undefined,
          category: categoryFilter ? [categoryFilter] : undefined,
          platform: platformFilter ? [platformFilter] : undefined,
        },
        analysisType: 'all',
      }),
    onSuccess: () => {
      setShowAnalysis(true);
    },
  });

  const handleStatusChange = (newStatus: string) => {
    if (!selectedReport) return;
    updateMutation.mutate({ id: selectedReport, data: { status: newStatus } });
  };

  const handleSaveNotes = () => {
    if (!selectedReport) return;
    updateMutation.mutate({ id: selectedReport, data: { admin_notes: adminNotes } });
  };

  const report = reportDetails?.report;

  // Calculate stats
  const totalReports = stats?.total || 0;
  const newReports = stats?.byStatus?.new || 0;
  const investigatingReports = stats?.byStatus?.investigating || 0;
  const resolvedReports = stats?.byStatus?.resolved || 0;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bug className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Reports</p>
              <p className="text-2xl font-bold text-gray-900">{totalReports}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">New</p>
              <p className="text-2xl font-bold text-gray-900">{newReports}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Investigating</p>
              <p className="text-2xl font-bold text-gray-900">{investigatingReports}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">{resolvedReports}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] max-w-xs relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search reports..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </select>

          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Platforms</option>
            {PLATFORM_OPTIONS.map((p) => (
              <option key={p} value={p} className="capitalize">{p}</option>
            ))}
          </select>

          <button
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg text-sm font-medium hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 transition-all"
          >
            {analyzeMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Analyze with AI
          </button>
        </div>
      </div>

      {/* AI Analysis Panel */}
      {showAnalysis && analyzeMutation.data && (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">
                AI Analysis ({analyzeMutation.data.reportCount} reports)
              </h3>
            </div>
            <button
              onClick={() => setShowAnalysis(false)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-purple-800 mb-1">Summary</h4>
              <p className="text-sm text-gray-700">{analyzeMutation.data.analysis.summary}</p>
            </div>

            {analyzeMutation.data.analysis.commonPatterns.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-purple-800 mb-1">Common Patterns</h4>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {analyzeMutation.data.analysis.commonPatterns.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}

            {analyzeMutation.data.analysis.affectedAreas.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-purple-800 mb-1">Affected Areas</h4>
                <div className="flex flex-wrap gap-2">
                  {analyzeMutation.data.analysis.affectedAreas.map((a, i) => (
                    <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {analyzeMutation.data.analysis.recommendations.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-purple-800 mb-1">Recommendations</h4>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {analyzeMutation.data.analysis.recommendations.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reports Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reporter</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading || isFetching ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : reportsData?.reports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    No bug reports found
                  </td>
                </tr>
              ) : (
                reportsData?.reports.map((r: BugReport) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {CATEGORY_LABELS[r.category] || r.category}
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-xs">
                        <p className="text-sm text-gray-900 truncate">
                          {r.description_english || r.description}
                        </p>
                        {r.was_translated && (
                          <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                            <Languages className="w-3 h-3" />
                            Translated from {r.detected_language?.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        {PLATFORM_ICONS[r.platform || ''] || <Globe className="w-4 h-4" />}
                        <span className="text-sm capitalize">{r.platform || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-700'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {r.user_email || 'Anonymous'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setSelectedReport(r.id);
                          setAdminNotes(r.admin_notes || '');
                          setShowOriginal(false);
                        }}
                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {reportsData && reportsData.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-600">
              Page {page} of {reportsData.totalPages} ({reportsData.total} total)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(reportsData.totalPages, p + 1))}
                disabled={page === reportsData.totalPages}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setSelectedReport(null)}
            onKeyDown={(e) => e.key === 'Escape' && setSelectedReport(null)}
            role="button"
            tabIndex={0}
            aria-label="Close drawer"
          />
          <div className="ml-auto relative w-full max-w-xl bg-white shadow-xl flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-semibold text-lg">Bug Report Details</h2>
              <button
                onClick={() => setSelectedReport(null)}
                className="p-1.5 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingDetails ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : report ? (
              <div className="flex-1 overflow-auto p-4 space-y-6">
                {/* Status */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">Status</span>
                  <div className="flex gap-2">
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleStatusChange(s)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                          report.status === s
                            ? STATUS_COLORS[s]
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">Category</span>
                  <span className="text-gray-900">{CATEGORY_LABELS[report.category] || report.category}</span>
                </div>

                {/* Platform */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">Platform</span>
                  <div className="flex items-center gap-2">
                    {PLATFORM_ICONS[report.platform || ''] || <Globe className="w-4 h-4" />}
                    <span className="capitalize">{report.platform || 'Unknown'}</span>
                    {report.detected_country && (
                      <span className="text-gray-500">({report.detected_country})</span>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="block text-sm font-medium text-gray-700">Description</span>
                    {report.was_translated && (
                      <button
                        onClick={() => setShowOriginal(!showOriginal)}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700"
                      >
                        <Languages className="w-3 h-3" />
                        {showOriginal ? 'Show English' : `Show Original (${report.detected_language?.toUpperCase()})`}
                        {showOriginal ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-900 whitespace-pre-wrap">
                      {showOriginal ? report.description : (report.description_english || report.description)}
                    </p>
                  </div>
                </div>

                {/* Screenshot */}
                {report.screenshot_url && (
                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">Screenshot</span>
                    <a
                      href={report.screenshot_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        src={report.screenshot_url}
                        alt="Bug screenshot"
                        className="max-h-48 rounded-lg border object-contain"
                      />
                    </a>
                  </div>
                )}

                {/* System Info */}
                {report.system_info && Object.keys(report.system_info).length > 0 && (
                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">System Info</span>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                      {Object.entries(report.system_info).map(([key, value]) => (
                        <div key={key} className="flex">
                          <span className="text-gray-500 w-32 flex-shrink-0">{key}:</span>
                          <span className="text-gray-900">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reporter */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">Reporter</span>
                  <span className="text-gray-900">{report.user_email || 'Anonymous'}</span>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">Created</span>
                    <span className="text-gray-600 text-sm">{formatDate(report.created_at)}</span>
                  </div>
                  {report.resolved_at && (
                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-1">Resolved</span>
                      <span className="text-gray-600 text-sm">{formatDate(report.resolved_at)}</span>
                    </div>
                  )}
                </div>

                {/* Admin Notes */}
                <div>
                  <label htmlFor="admin-notes" className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                  <textarea
                    id="admin-notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this report..."
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    onClick={handleSaveNotes}
                    disabled={updateMutation.isPending}
                    className="mt-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
